/**
 * useWlCrmIntegration — Hook do painel WL para ler/salvar a configuração de
 * criação automática de negócio no CRM do tenant interno quando um novo
 * tenant cliente é cadastrado.
 *
 * Acesso 100% via RPCs `wl_*` (sem SELECT direto em tabelas wl_*).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWlCrmIntegration');

/** Tag CRM (deal) usada no multi-select */
export interface WlInternalCrmDealTag {
  id: string;
  name: string;
  color: string | null;
}

/** Estrutura retornada por wl_get_crm_integration */
export interface WlCrmIntegrationData {
  internal_tenant_configured: boolean;
  internal_tenant_id: string | null;
  is_configured: boolean;
  enabled: boolean;
  workspace_id: string | null;
  pipeline_id: string | null;
  phase_id: string | null;
  source: string | null;
  seller_assignment_mode: 'none' | 'manual' | 'distribution_queue';
  seller_id: string | null;
  distribution_queue_id: string | null;
  /** IDs das tags aplicadas automaticamente ao deal criado */
  deal_tag_ids: string[];
  /** Tags enriquecidas (id/name/color) para render dos chips selecionados */
  deal_tags: WlInternalCrmDealTag[];
  updated_by: string | null;
  updated_at: string | null;
}

/** Payload aceito por wl_set_crm_integration */
export interface WlCrmIntegrationPayload {
  enabled: boolean;
  workspace_id: string | null;
  pipeline_id: string | null;
  phase_id: string | null;
  source: string | null;
  seller_assignment_mode: 'none' | 'manual' | 'distribution_queue';
  seller_id: string | null;
  distribution_queue_id: string | null;
  /** IDs das tags do CRM aplicadas ao deal recém-criado (opcional) */
  deal_tag_ids: string[];
}

/** Itens auxiliares para os selects */
export interface WlInternalCrmPhase {
  id: string;
  name: string;
  color: string | null;
  probability_percent: number | null;
  is_won: boolean;
  is_lost: boolean;
}
export interface WlInternalCrmPipeline {
  id: string;
  name: string;
  phases: WlInternalCrmPhase[];
}
export interface WlInternalCrmWorkspace {
  id: string;
  name: string;
  pipelines: WlInternalCrmPipeline[];
}
export interface WlInternalCrmQueue {
  id: string;
  name: string;
  distribution_method: string;
  context_type: string;
}
export interface WlInternalCrmSeller {
  user_id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

/**
 * Hook principal: leitura + mutation de salvamento.
 */
export function useWlCrmIntegration() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['wl-crm-integration'],
    queryFn: async (): Promise<WlCrmIntegrationData> => {
      const { data, error } = await supabase.rpc('wl_get_crm_integration' as any);
      if (error) {
        logger.error('wl_get_crm_integration falhou', { error });
        throw error;
      }
      return data as unknown as WlCrmIntegrationData;
    },
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: async (payload: WlCrmIntegrationPayload): Promise<WlCrmIntegrationData> => {
      const { data, error } = await supabase.rpc('wl_set_crm_integration' as any, {
        p_payload: payload as any,
      });
      if (error) {
        logger.error('wl_set_crm_integration falhou', { error });
        throw error;
      }
      return data as unknown as WlCrmIntegrationData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wl-crm-integration'] });
      toast.success('Configuração salva', {
        description: 'Integração CRM atualizada com sucesso.',
      });
    },
    onError: (err: any) => {
      toast.error('Erro ao salvar configuração', {
        description: err?.message || 'Erro inesperado',
      });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}

/** Lista workspaces+pipelines+phases do tenant interno */
export function useWlInternalCrmWorkspaces(enabled: boolean) {
  return useQuery({
    queryKey: ['wl-internal-crm-workspaces'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<WlInternalCrmWorkspace[]> => {
      const { data, error } = await supabase.rpc('wl_list_internal_crm_workspaces' as any);
      if (error) throw error;
      return (data as unknown as WlInternalCrmWorkspace[]) || [];
    },
  });
}

/** Lista filas de distribuição CRM do tenant interno */
export function useWlInternalCrmQueues(enabled: boolean) {
  return useQuery({
    queryKey: ['wl-internal-crm-queues'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<WlInternalCrmQueue[]> => {
      const { data, error } = await supabase.rpc('wl_list_internal_crm_queues' as any);
      if (error) throw error;
      return (data as unknown as WlInternalCrmQueue[]) || [];
    },
  });
}

/** Lista tags de deal (CRM) do tenant interno para o multi-select */
export function useWlInternalCrmDealTags(enabled: boolean) {
  return useQuery({
    queryKey: ['wl-internal-crm-deal-tags'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<WlInternalCrmDealTag[]> => {
      const { data, error } = await supabase.rpc('wl_list_internal_crm_deal_tags' as any);
      if (error) throw error;
      return (data as unknown as WlInternalCrmDealTag[]) || [];
    },
  });
}

/** Lista vendedores (membros) do tenant interno */
export function useWlInternalCrmSellers(enabled: boolean) {
  return useQuery({
    queryKey: ['wl-internal-crm-sellers'],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<WlInternalCrmSeller[]> => {
      const { data, error } = await supabase.rpc('wl_list_internal_crm_sellers' as any);
      if (error) throw error;
      return (data as unknown as WlInternalCrmSeller[]) || [];
    },
  });
}
